import type { DefaultValues, UseFormReturn } from 'react-hook-form';
import { queryString } from '@olivierpascal/helpers';
import { Field, Labeled, TextInputField } from '@sixui/ui';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import type { IContainerProps } from '~/components/utils/Container';
import type { ILogInFormValues } from './LogInForm';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';
import { Link } from '~/components/atoms/Link';
import { AppRegionSelector } from '~/components/organisms/fields/AppRegionSelector';

export type ILogInFormMainSectionProps = IContainerProps & {
  form: UseFormReturn<ILogInFormValues>;
  defaultValues?: DefaultValues<ILogInFormValues>;
};

export const LogInFormMainSection: React.FC<ILogInFormMainSectionProps> = (
  props,
) => {
  const { sx, form, defaultValues } = props;
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const nextUrl = searchParams.get('nextUrl') ?? undefined;
  const email = form.watch('email');
  const appRegion = form.watch('appRegion');
  const qs = queryString.build({
    nextUrl,
    email,
    appRegion,
  });
  const resetUrl = `^/reset-password${qs ? `?${qs}` : ''}`;

  return (
    <FormSection sx={sx}>
      <Labeled
        label={t('common.emailAddress')}
        required
        readOnly={defaultValues?.email !== undefined}
      >
        <FormField
          name="email"
          control={form.control}
          as={TextInputField}
          forwardError
          type="email"
          autoComplete="email"
          autoCapitalize="off"
        />
      </Labeled>
      <Labeled
        label={t('common.password')}
        required
        trailingAction={
          <Link to={resetUrl}>{t('LogInForm.forgot-password')}</Link>
        }
      >
        <FormField
          name="password"
          control={form.control}
          as={TextInputField}
          forwardError
          type="password"
          autoComplete="current-password"
        />
      </Labeled>

      <Labeled label={t('common.region')} required>
        {defaultValues?.appRegion ? (
          <Field readOnly>{defaultValues?.appRegion}</Field>
        ) : (
          <FormField
            name="appRegion"
            control={form.control}
            as={AppRegionSelector}
            forwardError
          />
        )}
      </Labeled>
    </FormSection>
  );
};
